import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronRight, ChevronUp, Trash2 } from 'lucide-react';
import type {
  DeviceModel,
  DevicePortRole,
  PoEInSpec,
  PoEOutSpec,
  PoEStandard,
  PowerCapacityLimit,
  PowerInput,
  PowerInputKind,
  PowerOutput,
  PowerOutputKind,
  PowerSelectMode,
  PortRoleKind,
  DevicePowerClass,
} from '../../types/deviceModel';
import {
  POE_STANDARDS,
  POE_STANDARD_LABEL,
  selfDrawWattsAtVoltage,
  inputCapacityLimit,
  typicalVoltageOfInput,
  typicalDeviceVoltage,
  selfDrawLimitOf,
  devicePowerClass,
} from '../../types/deviceModel';
import type { EquipmentFace, PortRect } from '../../types';
import { validateDeviceModel } from '../../engine/networkLogic';
import type { FacedPort } from '../../engine/networkLogic/layoutPorts';
import { poeStandardsProduceableByInputs } from '../../engine/networkLogic/validateDeviceModel';
import {
  applyRoleToPort,
  createPortRole,
  facedPortKey,
  portHasRole,
  synthesizePortRoles,
  syncRoleSpecToPorts,
  unapplyRoleFromPort,
} from '../../engine/networkLogic/portRoles';
import {
  defaultPoeIn,
  defaultPoeOut,
  mergePortsIntoDraft,
} from '../../engine/networkLogic/electricalDraft';
import { layoutPortKey } from '../../utils/portLayout';
import { poeRj45Look, poeRj45Caption } from '../../utils/poeRj45Look';
import { PoeRj45Marks } from '../Canvas/PoeRj45Marks';

type FormTab = 'power' | 'outputs' | 'ports';
type FormMenu = 'role' | 'input' | 'output' | null;
type PoeRule = 'follow-voltage' | 'poe-out-dc-only' | 'pd-only';

const INPUT_KINDS: Array<{ id: PowerInputKind; label: string }> = [
  { id: 'ac', label: 'AC inlet' },
  { id: 'dc-jack', label: 'DC jack' },
  { id: 'dc-terminal', label: '2-pin DC' },
  { id: 'poe-in', label: 'PoE-in (on a port)' },
  { id: 'pv', label: 'PV / MPPT' },
  { id: 'battery', label: 'Battery DC' },
];

const OUTPUT_KINDS: Array<{ id: PowerOutputKind; label: string }> = [
  { id: 'ac', label: 'AC outlet' },
  { id: 'dc', label: 'DC out' },
];

const INPUT_KIND_LABEL: Record<PowerInputKind, string> = {
  ac: 'AC inlet',
  'dc-jack': 'DC jack',
  'dc-terminal': '2-pin DC',
  'poe-in': 'PoE-in',
  pv: 'PV / MPPT',
  battery: 'Battery DC',
};

export function selectChrome(isDark: boolean): React.CSSProperties {
  const stroke = isDark ? '#e5e7eb' : '#111827';
  const chevron = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`
  );
  return {
    appearance: 'none',
    WebkitAppearance: 'none',
    colorScheme: isDark ? 'dark' : 'light',
    backgroundColor: isDark ? '#171a1f' : '#ffffff',
    color: isDark ? '#e5e7eb' : '#111827',
    backgroundImage: `url("data:image/svg+xml,${chevron}")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.5rem center',
    backgroundSize: '14px 14px',
    paddingRight: '1.6rem',
  };
}

export function optionChrome(isDark: boolean): React.CSSProperties {
  return {
    backgroundColor: isDark ? '#171a1f' : '#ffffff',
    color: isDark ? '#e5e7eb' : '#111827',
  };
}

function MenuDropdown({
  id,
  menu,
  setMenu,
  label,
  children,
  isDark,
  showChevron = true,
}: {
  id: Exclude<FormMenu, null>;
  menu: FormMenu;
  setMenu: (m: FormMenu) => void;
  label: React.ReactNode;
  children: React.ReactNode;
  isDark: boolean;
  showChevron?: boolean;
}) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const open = menu === id;
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setMenu(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, setMenu]);

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={() => {
          if (open) {
            setMenu(null);
            return;
          }
          const r = btnRef.current?.getBoundingClientRect();
          if (r) setPos({ top: r.bottom + 4, left: r.left });
          setMenu(id);
        }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-white/10 hover:bg-white/15"
      >
        {label}
        {showChevron && <ChevronDown size={14} className={`transition-transform ${open ? 'rotate-180' : ''}`} />}
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          className="fixed z-[220] min-w-[14rem] rounded border py-0.5 shadow-lg"
          style={{
            top: pos.top,
            left: pos.left,
            borderColor: isDark ? 'rgba(255,255,255,0.12)' : '#d1d5db',
            backgroundColor: isDark ? '#171a1f' : '#ffffff',
          }}
        >
          {children}
        </div>,
        document.body
      )}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  disabled,
  title,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      title={title}
      onClick={onClick}
      className="block w-full text-left px-3 py-1.5 hover:bg-white/10 disabled:opacity-40 disabled:hover:bg-transparent"
    >
      {label}
    </button>
  );
}

function formatVoltageRange(min?: number, max?: number): string {
  if (min == null && max == null) return '';
  if (min != null && (max == null || max === min)) return `${min}V`;
  if (min == null && max != null) return `${max}V`;
  return `${min}–${max}V`;
}

export interface ElectricalModelPanelProps {
  draft: DeviceModel;
  onChange: (next: DeviceModel) => void;
  ports: FacedPort[];
  onPortsChange: (next: FacedPort[], extra?: Partial<DeviceModel>) => void;
  isDark?: boolean;
  hoverPortId: string | null;
  onHoverPort: (layoutKey: string | null, face?: EquipmentFace) => void;
  compact?: boolean;
}

export const ElectricalModelPanel: React.FC<ElectricalModelPanelProps> = ({
  draft,
  onChange,
  ports,
  onPortsChange,
  isDark = true,
  hoverPortId,
  onHoverPort,
  compact = true,
}) => {
  const [expandedRoleIds, setExpandedRoleIds] = useState<string[]>(
    () => (draft.portRoles?.[0]?.id ? [draft.portRoles[0].id] : [])
  );
  const [tab, setTab] = useState<FormTab>('power');
  const [menu, setMenu] = useState<FormMenu>(null);

  const validation = useMemo(() => validateDeviceModel(draft), [draft]);
  const produceable = useMemo(
    () => poeStandardsProduceableByInputs(draft.powerInputs, true),
    [draft.powerInputs]
  );

  const dcOrAc = draft.powerInputs.filter((i) => i.kind === 'ac' || i.kind === 'dc-jack' || i.kind === 'dc-terminal');
  const powerSelect: PowerSelectMode = draft.powerSelect || 'highest-voltage';
  const poeOutSpecs = [
    ...(draft.portRoles || []).filter((r) => r.kind === 'poe-out' && r.poeOut).map((r) => r.poeOut!),
    ...ports.filter((p) => p.poeOut).map((p) => p.poeOut!),
  ];
  const poeRule: PoeRule =
    dcOrAc.length === 0 && poeOutSpecs.length === 0
      ? 'pd-only'
      : poeOutSpecs.some((s) => (s.poeOutOnlyWhenPoweredBy || []).some((k) => k === 'dc-jack' || k === 'dc-terminal'))
        ? 'poe-out-dc-only'
        : 'follow-voltage';

  const setPorts = (next: FacedPort[], extra?: Partial<DeviceModel>) => {
    const merged = mergePortsIntoDraft(draft, next, extra);
    onPortsChange(merged.ports, extra);
    onChange(merged.draft);
  };

  const updatePort = (key: string, patch: Partial<PortRect>) => {
    setPorts(
      ports.map((p) => {
        if (facedPortKey(p) !== key) return p;
        return { ...p, ...patch };
      })
    );
  };

  const patchInput = (id: string, patch: Partial<PowerInput>) => {
    const powerInputs = draft.powerInputs.map((i) => (i.id === id ? { ...i, ...patch } : i));
    const input = powerInputs.find((i) => i.id === id);
    if (!input || input.kind !== 'poe-in') {
      onChange({ ...draft, powerInputs });
      return;
    }
    let nextPorts = ports.map((p) => {
      if (p.number !== input.portNumber) return p;
      if ((input.face || 'front') !== (p.face || 'front')) return p;
      if (!p.poeIn) return p;
      return {
        ...p,
        poeIn: {
          ...p.poeIn,
          voltageMin: input.voltageMin ?? p.poeIn.voltageMin,
          voltageMax: input.voltageMax ?? p.poeIn.voltageMax,
          maxWatts: input.maxWatts ?? p.poeIn.maxWatts,
        },
      };
    });
    let portRoles = draft.portRoles;
    const bound = nextPorts.find(
      (p) => p.poeIn && p.number === input.portNumber && (p.face || 'front') === (input.face || 'front')
    );
    if (bound?.poeIn && bound.poeInRoleId && portRoles) {
      const role = portRoles.find((r) => r.id === bound.poeInRoleId);
      if (role?.poeIn) {
        const nextRole: DevicePortRole = {
          ...role,
          poeIn: {
            ...role.poeIn,
            voltageMin: bound.poeIn.voltageMin,
            voltageMax: bound.poeIn.voltageMax,
            maxWatts: bound.poeIn.maxWatts,
          },
        };
        portRoles = portRoles.map((r) => (r.id === nextRole.id ? nextRole : r));
        nextPorts = syncRoleSpecToPorts(nextPorts, nextRole);
      }
    }
    setPorts(nextPorts, { powerInputs, portRoles });
  };

  const addInput = (kind: PowerInputKind) => {
    const id = `${kind}-${Date.now()}`;
    const input: PowerInput = { id, kind, enabled: true, capacityLimit: 'watts' };
    if (kind === 'dc-jack' || kind === 'dc-terminal') {
      input.voltageMin = 24;
      input.voltageMax = 57;
      input.maxWatts = 60;
    }
    if (kind === 'ac') {
      input.voltageMin = 100;
      input.voltageMax = 240;
      input.maxWatts = 200;
    }
    if (kind === 'pv') {
      const plus = ports.find((p) => String(p.kind) === 'pvPlus');
      const minus = ports.find((p) => String(p.kind) === 'pvMinus');
      input.mpptId = id;
      input.plusPortNumber = plus?.number;
      input.minusPortNumber = minus?.number;
      input.plusFace = plus?.face;
      input.minusFace = minus?.face;
      input.voltageMax = 500;
      input.mpptVoltageMin = 150;
      input.mpptVoltageMax = 425;
      input.maxWatts = 3000;
    }
    if (kind === 'battery') {
      const plus = ports.find((p) => String(p.kind) === 'batPlus');
      const minus = ports.find((p) => String(p.kind) === 'batMinus');
      input.plusPortNumber = plus?.number;
      input.minusPortNumber = minus?.number;
      input.voltageMin = 40;
      input.voltageMax = 60;
      input.maxAmps = 100;
      input.capacityLimit = 'amps';
    }
    if (kind === 'poe-in') {
      const rj = ports.find((p) => p.kind === 'rj45') || ports[0];
      input.portNumber = rj?.number || 1;
      input.face = rj?.face || 'front';
      input.voltageMin = rj?.poeIn?.voltageMin ?? 44;
      input.voltageMax = rj?.poeIn?.voltageMax ?? 57;
      input.maxWatts = rj?.poeIn?.maxWatts ?? 15;
    }
    if (kind !== 'poe-in') {
      onChange({ ...draft, powerInputs: [...draft.powerInputs, input] });
      return;
    }
    const nextPorts = ports.map((p) => {
      if (p.number !== input.portNumber) return p;
      if ((input.face || 'front') !== (p.face || 'front')) return p;
      if (p.poeIn) return p;
      return { ...p, poeIn: defaultPoeIn() };
    });
    setPorts(nextPorts, { powerInputs: [...draft.powerInputs, input] });
  };

  const removeInput = (id: string) => {
    const removed = draft.powerInputs.find((i) => i.id === id);
    const nextInputs = draft.powerInputs.filter((i) => i.id !== id);
    let nextPorts = ports;
    if (removed?.kind === 'poe-in') {
      nextPorts = ports.map((p) => {
        if (p.number !== removed.portNumber) return p;
        if ((removed.face || 'front') !== (p.face || 'front')) return p;
        const { poeIn: _in, poeInRoleId: _rid, ...rest } = p;
        return rest as FacedPort;
      });
    }
    if (nextInputs.filter((i) => i.kind === 'ac' || i.kind === 'dc-jack' || i.kind === 'dc-terminal').length === 0) {
      nextPorts = nextPorts.map((p) => {
        const { poeOut: _o, poeOutRoleId: _rid, ...rest } = p;
        return rest as FacedPort;
      });
    }
    setPorts(nextPorts, { powerInputs: nextInputs });
  };

  const moveInput = (id: string, dir: -1 | 1) => {
    const idx = draft.powerInputs.findIndex((i) => i.id === id);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= draft.powerInputs.length) return;
    const list = [...draft.powerInputs];
    const [row] = list.splice(idx, 1);
    list.splice(next, 0, row);
    onChange({ ...draft, powerInputs: list });
  };

  const applyPreset = (preset: PoeRule) => {
    if (preset === 'pd-only') {
      const poeInputs = draft.powerInputs.filter((i) => i.kind === 'poe-in');
      const nextPorts = ports.map((p) => {
        if (p.kind !== 'rj45') return p;
        const { poeOut: _o, poeOutRoleId: _rid, ...rest } = p;
        return {
          ...rest,
          poeIn: rest.poeIn || defaultPoeIn(),
        } as FacedPort;
      });
      const { roles, ports: stamped } = synthesizePortRoles(nextPorts, []);
      setPorts(stamped, {
        portRoles: roles,
        powerInputs: poeInputs.length
          ? poeInputs
          : [{ id: 'poe-in', kind: 'poe-in', portNumber: ports.find((p) => p.kind === 'rj45')?.number || 1, face: 'front', voltageMin: 44, voltageMax: 57 }],
      });
      return;
    }
    const dcOnly: PoEOutSpec['poeOutOnlyWhenPoweredBy'] = ['dc-jack', 'dc-terminal'];
    const nextPorts: FacedPort[] = ports.map((p) => {
      if (!p.poeOut) return p;
      return {
        ...p,
        poeOut: {
          ...p.poeOut,
          outputFollowsInputVoltage: preset === 'follow-voltage',
          poeOutOnlyWhenPoweredBy: preset === 'poe-out-dc-only' ? dcOnly : p.poeOut.poeOutOnlyWhenPoweredBy,
        },
      };
    });
    const nextRoles: DevicePortRole[] = (draft.portRoles || []).map((r) => {
      if (r.kind !== 'poe-out' || !r.poeOut) return r;
      return {
        ...r,
        poeOut: {
          ...r.poeOut,
          outputFollowsInputVoltage: preset === 'follow-voltage',
          poeOutOnlyWhenPoweredBy: preset === 'poe-out-dc-only' ? dcOnly : r.poeOut.poeOutOnlyWhenPoweredBy,
        },
      };
    });
    setPorts(nextPorts, { portRoles: nextRoles });
  };

  const roles = draft.portRoles || [];
  useEffect(() => {
    const ids = new Set(roles.map((r) => r.id));
    setExpandedRoleIds((prev) => {
      const next = prev.filter((id) => ids.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [roles]);

  const toggleRoleExpanded = (roleId: string) => {
    setExpandedRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((id) => id !== roleId) : prev.concat(roleId)
    );
  };

  const canPoeOut = dcOrAc.length > 0 || draft.powerInputs.some((i) => i.kind === 'poe-in');
  const selfDrawLimit = selfDrawLimitOf(draft);
  const selfDrawValue = selfDrawLimit === 'amps' ? draft.selfDrawAmps ?? 0 : draft.selfDrawWatts ?? 0;

  const setSelfDrawValue = (raw: number) => {
    const n = Number.isFinite(raw) && raw >= 0 ? raw : 0;
    const limit = selfDrawLimitOf(draft);
    if (limit === 'amps') {
      onChange({
        ...draft,
        selfDrawLimit: 'amps',
        selfDrawAmps: n,
        selfDrawWatts: selfDrawWattsAtVoltage({ ...draft, selfDrawLimit: 'amps', selfDrawAmps: n }),
      });
      return;
    }
    onChange({ ...draft, selfDrawLimit: 'watts', selfDrawWatts: n, selfDrawAmps: undefined });
  };

  const setSelfDrawLimit = (next: PowerCapacityLimit) => {
    const current = selfDrawLimitOf(draft);
    if (next === current) return;
    const v = typicalDeviceVoltage(draft);
    if (next === 'amps') {
      const amps =
        draft.selfDrawWatts != null && v != null && v > 0
          ? Math.round((draft.selfDrawWatts / v) * 1000) / 1000
          : draft.selfDrawAmps ?? 0;
      onChange({
        ...draft,
        selfDrawLimit: 'amps',
        selfDrawAmps: amps,
        selfDrawWatts: selfDrawWattsAtVoltage({ ...draft, selfDrawLimit: 'amps', selfDrawAmps: amps }),
      });
      return;
    }
    const watts =
      draft.selfDrawAmps != null && v != null && v > 0
        ? Math.round(draft.selfDrawAmps * v * 10) / 10
        : draft.selfDrawWatts;
    onChange({ ...draft, selfDrawLimit: 'watts', selfDrawWatts: watts, selfDrawAmps: undefined });
  };

  const addRole = (kind: PortRoleKind) => {
    setMenu(null);
    if (kind === 'poe-out' && !canPoeOut) return;
    const spec = kind === 'poe-in' ? defaultPoeIn() : defaultPoeOut(produceable);
    const role = createPortRole(kind, spec, roles);
    onChange({ ...draft, portRoles: [...(draft.portRoles || []), role] });
    setExpandedRoleIds((prev) => (prev.includes(role.id) ? prev : prev.concat(role.id)));
    setTab('ports');
  };

  const patchRole = (roleId: string, patch: Partial<DevicePortRole>) => {
    const current = roles.find((r) => r.id === roleId);
    if (!current) return;
    const nextRole = { ...current, ...patch };
    const nextRoles = roles.map((r) => (r.id === roleId ? nextRole : r));
    setPorts(syncRoleSpecToPorts(ports, nextRole), { portRoles: nextRoles });
  };

  const deleteRole = (roleId: string) => {
    const role = roles.find((r) => r.id === roleId);
    if (!role) return;
    const nextPorts = ports.map((p) => unapplyRoleFromPort(p, role));
    const nextRoles = roles.filter((r) => r.id !== roleId);
    setPorts(nextPorts, { portRoles: nextRoles });
  };

  const moveRole = (roleId: string, dir: -1 | 1) => {
    const idx = roles.findIndex((r) => r.id === roleId);
    const next = idx + dir;
    if (idx < 0 || next < 0 || next >= roles.length) return;
    const list = [...roles];
    const [row] = list.splice(idx, 1);
    list.splice(next, 0, row);
    onChange({ ...draft, portRoles: list });
  };

  const toggleRoleOnPort = (role: DevicePortRole, key: string, on: boolean) => {
    const nextPorts = ports.map((p) => {
      if (facedPortKey(p) !== key) return p;
      return on ? applyRoleToPort(p, role) : unapplyRoleFromPort(p, role);
    });
    setPorts(nextPorts, { portRoles: roles });
  };

  const setRoleOnCompatiblePorts = (role: DevicePortRole, on: boolean) => {
    const nextPorts = ports.map((p) => {
      if (String(p.kind) !== 'rj45') return p;
      if (role.kind === 'poe-out' && !canPoeOut) return p;
      return on ? applyRoleToPort(p, role) : unapplyRoleFromPort(p, role);
    });
    setPorts(nextPorts, { portRoles: roles });
  };

  const tabBtn = (id: FormTab, label: string) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={`px-4 py-2 text-sm rounded-md ${tab === id ? 'bg-blue-600 text-white' : 'bg-white/10'}`}
    >
      {label}
    </button>
  );

  return (
    <div className={`h-full min-h-0 overflow-y-auto text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
      <div className={`px-4 pt-3 grid gap-3 ${compact ? 'grid-cols-1' : 'grid-cols-2'}`}>
        <label className="block">
          <span className="text-sm text-gray-400">Self Consumption</span>
          <div className="flex mt-1 gap-1 min-w-0">
            <input
              type="number"
              min={0}
              step="0.01"
              value={selfDrawValue}
              onChange={(e) => setSelfDrawValue(e.target.value === '' ? 0 : Number(e.target.value))}
              className="flex-1 min-w-0 bg-black/20 border border-white/10 rounded px-2.5 py-1.5"
              placeholder={selfDrawLimit === 'amps' ? 'A' : 'W'}
            />
            <select
              className={`shrink-0 rounded px-1.5 py-1.5 text-sm border appearance-none ${isDark ? 'border-white/10' : 'border-gray-300'}`}
              style={{ ...selectChrome(isDark), width: '3.4rem', paddingRight: '1.35rem' }}
              value={selfDrawLimit}
              onChange={(e) => setSelfDrawLimit(e.target.value as PowerCapacityLimit)}
              title="Rate self consumption in watts or amps"
            >
              <option value="watts" style={optionChrome(isDark)}>W</option>
              <option value="amps" style={optionChrome(isDark)}>A</option>
            </select>
          </div>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={!!draft.isPassthroughDevice}
            onChange={(e) => onChange({ ...draft, isPassthroughDevice: e.target.checked })}
          />
          Patch panel / passthrough
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-400">Device class</span>
          <select
            className={`rounded px-2.5 py-1.5 text-sm border appearance-none ${isDark ? 'border-white/10' : 'border-gray-300'}`}
            style={selectChrome(isDark)}
            value={devicePowerClass(draft)}
            onChange={(e) => {
              const next = e.target.value as DevicePowerClass;
              const wasInverter = devicePowerClass(draft) === 'inverter';
              onChange({
                ...draft,
                isSolar: next === 'solar',
                isBattery: next === 'battery',
                isInverter: next === 'inverter',
                isPsu: next === 'psu',
                isPassthroughDevice: next === 'none' ? draft.isPassthroughDevice : false,
                requiresBatteryToStart:
                  next === 'inverter' ? (wasInverter ? draft.requiresBatteryToStart !== false : true) : undefined,
              });
            }}
          >
            <option value="none" style={optionChrome(isDark)}>Standard</option>
            <option value="solar" style={optionChrome(isDark)}>Solar panel</option>
            <option value="battery" style={optionChrome(isDark)}>Battery</option>
            <option value="inverter" style={optionChrome(isDark)}>Inverter</option>
            <option value="psu" style={optionChrome(isDark)}>PSU</option>
          </select>
        </label>
      </div>

      {devicePowerClass(draft) === 'solar' && (
        <div className="px-4 pt-3 grid grid-cols-2 gap-2">
          {([
            ['pvStcWatts', 'STC W'],
            ['pvVoc', 'Voc'],
            ['pvVmp', 'Vmp'],
            ['pvIsc', 'Isc'],
            ['pvImp', 'Imp'],
          ] as const).map(([key, label]) => (
            <label key={key} className="block text-xs text-gray-400">
              {label}
              <input
                type="number"
                className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm"
                value={draft[key] ?? ''}
                onChange={(e) => onChange({ ...draft, [key]: e.target.value === '' ? undefined : Number(e.target.value) })}
              />
            </label>
          ))}
        </div>
      )}
      {devicePowerClass(draft) === 'battery' && (
        <div className="px-4 pt-3 grid grid-cols-3 gap-2">
          <label className="block text-xs text-gray-400">
            Nominal V
            <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={draft.nominalVoltage ?? ''} onChange={(e) => onChange({ ...draft, nominalVoltage: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </label>
          <label className="block text-xs text-gray-400">
            Capacity Ah
            <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={draft.capacityAh ?? ''} onChange={(e) => onChange({ ...draft, capacityAh: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </label>
          <label className="block text-xs text-gray-400">
            Usable %
            <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={draft.usableFraction != null ? Math.round(draft.usableFraction * 100) : 80} onChange={(e) => onChange({ ...draft, usableFraction: Number(e.target.value) / 100 })} />
          </label>
        </div>
      )}
      {devicePowerClass(draft) === 'inverter' && (
        <div className="px-4 pt-3 grid grid-cols-2 gap-2">
          <label className="block text-xs text-gray-400">
            Efficiency %
            <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={draft.inverterEfficiency != null ? Math.round(draft.inverterEfficiency * 100) : 97} onChange={(e) => onChange({ ...draft, inverterEfficiency: Number(e.target.value) / 100 })} />
          </label>
          <label className="block text-xs text-gray-400">
            Max discharge A
            <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={draft.maxDischargeAmps ?? ''} onChange={(e) => onChange({ ...draft, maxDischargeAmps: e.target.value === '' ? undefined : Number(e.target.value) })} />
          </label>
          <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={draft.requiresBatteryToStart !== false}
              onChange={(e) => onChange({ ...draft, requiresBatteryToStart: e.target.checked })}
            />
            Requires battery to start
          </label>
        </div>
      )}

      <div className="px-4 pt-3 flex gap-2">
        {tabBtn('power', 'Power Inputs')}
        {tabBtn('outputs', 'Power Output')}
        {tabBtn('ports', 'Port Roles')}
      </div>

      <div className="p-4 pb-5 text-sm">
        {tab === 'power' && (
          <div>
            <p className="text-xs text-gray-500 mb-3">
              AC/DC ports on the layout are connectors. Power Inputs below are how Logic powers this device.
            </p>
            <div className="flex flex-col gap-2 mb-3 relative z-10">
              <MenuDropdown id="input" menu={menu} setMenu={setMenu} label="+ Add Power Input" isDark={isDark} showChevron={false}>
                {INPUT_KINDS.map((k) => (
                  <MenuItem
                    key={k.id}
                    label={k.label}
                    onClick={() => {
                      addInput(k.id);
                      setMenu(null);
                    }}
                  />
                ))}
              </MenuDropdown>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">Power Priority</span>
                <select
                  className={`rounded px-2.5 py-1.5 text-sm border appearance-none ${isDark ? 'border-white/10' : 'border-gray-300'}`}
                  style={selectChrome(isDark)}
                  value={powerSelect}
                  onChange={(e) => onChange({ ...draft, powerSelect: e.target.value as PowerSelectMode })}
                >
                  <option value="highest-voltage" style={optionChrome(isDark)}>Highest voltage first</option>
                  <option value="listed-order" style={optionChrome(isDark)}>Listed order</option>
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">PoE Rule</span>
                <select
                  className={`rounded px-2.5 py-1.5 text-sm border appearance-none ${isDark ? 'border-white/10' : 'border-gray-300'}`}
                  style={selectChrome(isDark)}
                  value={poeRule}
                  onChange={(e) => applyPreset(e.target.value as PoeRule)}
                >
                  <option value="follow-voltage" style={optionChrome(isDark)}>Follows input voltage</option>
                  <option value="poe-out-dc-only" style={optionChrome(isDark)}>Only when DC powered</option>
                  <option value="pd-only" style={optionChrome(isDark)}>PD only (no local PSU)</option>
                </select>
              </label>
            </div>
            {draft.powerInputs.length === 0 && (
              <p className="text-sm text-gray-400">No power inputs yet. Add AC, DC, or PoE-in.</p>
            )}
            {draft.powerInputs.map((input, idx) => (
              <PowerInputCard
                key={input.id}
                input={input}
                index={idx}
                total={draft.powerInputs.length}
                ports={ports}
                isDark={isDark}
                onMove={moveInput}
                onPatch={patchInput}
                onRemove={removeInput}
              />
            ))}
          </div>
        )}

        {tab === 'outputs' && (
          <div>
            <p className="text-xs text-gray-500 mb-3">
              Layout AC/DC ports are connectors. Power Output is what this device can feed to others (PSU DC, inverter AC).
            </p>
            <div className="mb-3">
              <MenuDropdown id="output" menu={menu} setMenu={setMenu} label="+ Add Power Output" isDark={isDark} showChevron={false}>
                {OUTPUT_KINDS.map((k) => (
                  <MenuItem
                    key={k.id}
                    label={k.label}
                    onClick={() => {
                      const id = `${k.id}-out-${Date.now()}`;
                      const output: PowerOutput = {
                        id,
                        kind: k.id,
                        enabled: true,
                        capacityLimit: 'watts',
                        voltageMin: k.id === 'ac' ? 220 : 24,
                        voltageMax: k.id === 'ac' ? 240 : 24,
                        maxWatts: k.id === 'ac' ? 5000 : 60,
                      };
                      onChange({ ...draft, powerOutputs: [...(draft.powerOutputs || []), output] });
                      setMenu(null);
                    }}
                  />
                ))}
              </MenuDropdown>
            </div>
            {(draft.powerOutputs || []).length === 0 && (
              <p className="text-sm text-gray-400">No power outputs yet.</p>
            )}
            {(draft.powerOutputs || []).map((output) => (
              <div key={output.id} className="rounded-md border p-3 mb-2.5 bg-white/5 border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium">{output.kind === 'ac' ? 'AC outlet' : 'DC out'}</div>
                  <button type="button" className="p-1.5 text-red-400 hover:bg-white/10 rounded" onClick={() => onChange({ ...draft, powerOutputs: (draft.powerOutputs || []).filter((o) => o.id !== output.id) })}>
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2.5">
                  <label className="block text-xs text-gray-400">
                    Min V
                    <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={output.voltageMin ?? ''} onChange={(e) => onChange({ ...draft, powerOutputs: (draft.powerOutputs || []).map((o) => o.id === output.id ? { ...o, voltageMin: e.target.value === '' ? undefined : Number(e.target.value) } : o) })} />
                  </label>
                  <label className="block text-xs text-gray-400">
                    Max V
                    <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={output.voltageMax ?? ''} onChange={(e) => onChange({ ...draft, powerOutputs: (draft.powerOutputs || []).map((o) => o.id === output.id ? { ...o, voltageMax: e.target.value === '' ? undefined : Number(e.target.value) } : o) })} />
                  </label>
                  <label className="block text-xs text-gray-400">
                    Max W
                    <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={output.maxWatts ?? ''} onChange={(e) => onChange({ ...draft, powerOutputs: (draft.powerOutputs || []).map((o) => o.id === output.id ? { ...o, maxWatts: e.target.value === '' ? undefined : Number(e.target.value) } : o) })} />
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'ports' && (
          <div className="flex flex-col gap-3">
            <div>
              <div className="mb-2">
                <MenuDropdown id="role" menu={menu} setMenu={setMenu} label="+ Add Role" isDark={isDark}>
                  <MenuItem label="PoE-in" onClick={() => addRole('poe-in')} />
                  <MenuItem
                    label={canPoeOut ? 'PoE-out' : 'PoE-out (needs power input)'}
                    disabled={!canPoeOut}
                    title={!canPoeOut ? 'Needs a power input' : undefined}
                    onClick={() => addRole('poe-out')}
                  />
                </MenuDropdown>
              </div>
              <div className="text-sm font-medium mb-1.5">Existing Roles</div>
              <div className="space-y-2">
                {roles.length === 0 && (
                  <p className="text-sm text-gray-400">No roles yet. Add PoE-in or PoE-out.</p>
                )}
                {roles.map((role, roleIndex) => {
                  const count = ports.filter((p) => portHasRole(p, role)).length;
                  const summary = role.kind === 'poe-in' && role.poeIn
                    ? poeInSummary(role.poeIn)
                    : role.kind === 'poe-out' && role.poeOut
                      ? poeOutSummary(role.poeOut)
                      : '';
                  const expanded = expandedRoleIds.includes(role.id);
                  const enabled = role.enabled !== false;
                  return (
                    <div
                      key={role.id}
                      className={`rounded-xl overflow-hidden bg-white/5 ${enabled ? '' : 'opacity-60'}`}
                    >
                      <div className="flex items-start bg-white/15">
                        <div className="flex flex-col shrink-0 mt-1.5 ml-1">
                          <button
                            type="button"
                            className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30"
                            disabled={roleIndex === 0}
                            onClick={() => moveRole(role.id, -1)}
                            title="Move role up"
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            type="button"
                            className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30"
                            disabled={roleIndex === roles.length - 1}
                            onClick={() => moveRole(role.id, 1)}
                            title="Move role down"
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleRoleExpanded(role.id)}
                          className="flex-1 min-w-0 flex items-center gap-2 px-2 py-2.5 text-left hover:bg-white/10"
                        >
                          {expanded
                            ? <ChevronDown size={16} className="shrink-0 text-gray-400" />
                            : <ChevronRight size={16} className="shrink-0 text-gray-400" />}
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate">{role.name}</div>
                            <div className="text-xs text-gray-400 truncate whitespace-nowrap">
                              {summary}{summary ? ' · ' : ''}{count === 1 ? '1 port' : `${count} ports`}
                              {!enabled ? ' · off' : ''}
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          className="shrink-0 mt-1.5 mr-1.5 p-1.5 rounded text-red-400 hover:bg-white/10"
                          title="Delete role"
                          onClick={() => deleteRole(role.id)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                      {expanded && (
                        <div className="px-3 pb-3">
                          <RolePanel
                            role={role}
                            ports={ports}
                            canPoeOut={canPoeOut}
                            produceable={produceable}
                            hoverPortId={hoverPortId}
                            onHoverPort={onHoverPort}
                            onRename={(name) => patchRole(role.id, { name })}
                            onToggleEnabled={(enabledNext) => patchRole(role.id, { enabled: enabledNext })}
                            onChangeSpec={(spec) => {
                              if (role.kind === 'poe-in') patchRole(role.id, { poeIn: spec as PoEInSpec });
                              else patchRole(role.id, { poeOut: spec as PoEOutSpec });
                            }}
                            onTogglePort={(key, on) => toggleRoleOnPort(role, key, on)}
                            onSelectAll={() => setRoleOnCompatiblePorts(role, true)}
                            onClear={() => setRoleOnCompatiblePorts(role, false)}
                            onTogglePassthrough={(key, on) => updatePort(key, { passthrough: on })}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {(validation.errors.length > 0 || validation.warnings.length > 0) && (
          <div className="mt-3 space-y-0.5">
            {validation.errors.map((e) => (
              <div key={e} className="text-sm text-red-400">{e}</div>
            ))}
            {validation.warnings.map((e) => (
              <div key={e} className="text-sm text-amber-400">{e}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function PowerInputCard({
  input,
  index,
  total,
  ports,
  isDark,
  onMove,
  onPatch,
  onRemove,
}: {
  input: PowerInput;
  index: number;
  total: number;
  ports: FacedPort[];
  isDark: boolean;
  onMove: (id: string, dir: -1 | 1) => void;
  onPatch: (id: string, patch: Partial<PowerInput>) => void;
  onRemove: (id: string) => void;
}) {
  const preview = formatVoltageRange(input.voltageMin, input.voltageMax);
  const enabled = input.enabled !== false;
  const limit = inputCapacityLimit(input);
  const num = (v: string, fallback?: number) => {
    if (v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const setLimit = (next: PowerCapacityLimit) => {
    if (next === limit) return;
    const v = typicalVoltageOfInput(input);
    if (next === 'amps') {
      const maxAmps =
        input.maxWatts != null && v != null && v > 0
          ? Math.round((input.maxWatts / v) * 1000) / 1000
          : input.maxAmps;
      onPatch(input.id, { capacityLimit: 'amps', maxAmps, maxWatts: undefined });
      return;
    }
    const maxWatts =
      input.maxAmps != null && v != null && v > 0
        ? Math.round(input.maxAmps * v * 10) / 10
        : input.maxWatts;
    onPatch(input.id, { capacityLimit: 'watts', maxWatts, maxAmps: undefined });
  };
  const ratingValue = limit === 'amps' ? input.maxAmps : input.maxWatts;
  const ratingPreview =
    limit === 'amps' && input.maxAmps != null
      ? `${input.maxAmps}A`
      : limit === 'watts' && input.maxWatts != null
        ? `${input.maxWatts}W`
        : '';

  return (
    <div className={`rounded-md border p-3 mb-2.5 ${enabled ? 'bg-white/5 border-white/10' : 'bg-white/[0.03] border-white/5 opacity-55'}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <div className="flex flex-col">
          <button type="button" className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30" disabled={index === 0} onClick={() => onMove(input.id, -1)} title="Higher priority when using list order">
            <ChevronUp size={16} />
          </button>
          <button type="button" className="p-0.5 text-gray-400 hover:text-white disabled:opacity-30" disabled={index === total - 1} onClick={() => onMove(input.id, 1)} title="Lower priority when using list order">
            <ChevronDown size={16} />
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-sm font-medium truncate">{INPUT_KIND_LABEL[input.kind]}</div>
            <label className="flex items-center gap-1.5 shrink-0 text-xs text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => onPatch(input.id, { enabled: e.target.checked })}
              />
              Enabled
            </label>
          </div>
          {preview && (
            <div className="text-xs text-gray-400">
              {preview}
              {ratingPreview ? ` · ${ratingPreview}` : ''}
            </div>
          )}
        </div>
        <button type="button" className="p-1.5 text-red-400 hover:bg-white/10 rounded" onClick={() => onRemove(input.id)} title="Remove">
          <Trash2 size={16} />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        <label className="block text-xs text-gray-400">
          Min V
          <input
            type="number"
            className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm text-gray-100"
            value={input.voltageMin ?? ''}
            placeholder={input.kind === 'ac' ? '100' : '12'}
            onChange={(e) => onPatch(input.id, { voltageMin: num(e.target.value, input.voltageMin) })}
          />
        </label>
        <label className="block text-xs text-gray-400">
          Max V
          <input
            type="number"
            className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm text-gray-100"
            value={input.voltageMax ?? ''}
            placeholder={input.kind === 'ac' ? '240' : '57'}
            onChange={(e) => onPatch(input.id, { voltageMax: num(e.target.value, input.voltageMax) })}
          />
        </label>
        <label className="block text-xs text-gray-400">
          Max
          <div className="flex mt-0.5 gap-1 min-w-0">
            <input
              type="number"
              className="flex-1 min-w-0 bg-black/20 rounded px-2 py-1.5 text-sm text-gray-100"
              value={ratingValue ?? ''}
              placeholder={limit === 'amps' ? 'A' : 'W'}
              onChange={(e) => {
                const n = num(e.target.value, ratingValue);
                if (limit === 'amps') {
                  onPatch(input.id, { capacityLimit: 'amps', maxAmps: n, maxWatts: undefined });
                } else {
                  onPatch(input.id, { capacityLimit: 'watts', maxWatts: n, maxAmps: undefined });
                }
              }}
            />
            <select
              className={`shrink-0 rounded px-1.5 py-1.5 text-sm border appearance-none ${isDark ? 'border-white/10' : 'border-gray-300'}`}
              style={{ ...selectChrome(isDark), width: '3.4rem', paddingRight: '1.35rem' }}
              value={limit}
              onChange={(e) => setLimit(e.target.value as PowerCapacityLimit)}
              title="Rate this input in watts or amps"
            >
              <option value="watts" style={optionChrome(isDark)}>W</option>
              <option value="amps" style={optionChrome(isDark)}>A</option>
            </select>
          </div>
        </label>
      </div>
      {input.kind === 'poe-in' && (
        <label className="block text-xs text-gray-400 mt-2.5">
          Bound port
          <select
            className={`w-full mt-0.5 rounded px-2 py-1.5 text-sm border appearance-none ${isDark ? 'border-white/10' : 'border-gray-300'}`}
            style={selectChrome(isDark)}
            value={`${input.face || 'front'}:${input.portNumber || ''}`}
            onChange={(e) => {
              const [face, numStr] = e.target.value.split(':');
              onPatch(input.id, { face: face as PowerInput['face'], portNumber: Number(numStr) });
            }}
          >
            {ports.filter((p) => p.kind === 'rj45').map((p) => (
              <option key={`${p.face}:${p.number}:${p.kind}`} value={`${p.face}:${p.number}`} style={optionChrome(isDark)}>
                {p.face} {p.label || p.number}
              </option>
            ))}
          </select>
        </label>
      )}
      {(input.kind === 'pv' || input.kind === 'battery') && (
        <div className="grid grid-cols-2 gap-2.5 mt-2.5">
          <label className="block text-xs text-gray-400">
            {input.kind === 'pv' ? 'PV+' : 'BAT+'}
            <select
              className={`w-full mt-0.5 rounded px-2 py-1.5 text-sm border appearance-none ${isDark ? 'border-white/10' : 'border-gray-300'}`}
              style={selectChrome(isDark)}
              value={`${input.plusFace || 'front'}:${input.plusPortNumber || ''}`}
              onChange={(e) => {
                const [face, numStr] = e.target.value.split(':');
                onPatch(input.id, { plusFace: face as PowerInput['face'], plusPortNumber: Number(numStr) });
              }}
            >
              {ports.filter((p) => String(p.kind) === (input.kind === 'pv' ? 'pvPlus' : 'batPlus')).map((p) => (
                <option key={`${p.face}:${p.number}:${p.kind}`} value={`${p.face}:${p.number}`} style={optionChrome(isDark)}>
                  {p.label || p.number}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-xs text-gray-400">
            {input.kind === 'pv' ? 'PV−' : 'BAT−'}
            <select
              className={`w-full mt-0.5 rounded px-2 py-1.5 text-sm border appearance-none ${isDark ? 'border-white/10' : 'border-gray-300'}`}
              style={selectChrome(isDark)}
              value={`${input.minusFace || 'front'}:${input.minusPortNumber || ''}`}
              onChange={(e) => {
                const [face, numStr] = e.target.value.split(':');
                onPatch(input.id, { minusFace: face as PowerInput['face'], minusPortNumber: Number(numStr) });
              }}
            >
              {ports.filter((p) => String(p.kind) === (input.kind === 'pv' ? 'pvMinus' : 'batMinus')).map((p) => (
                <option key={`${p.face}:${p.number}:${p.kind}`} value={`${p.face}:${p.number}`} style={optionChrome(isDark)}>
                  {p.label || p.number}
                </option>
              ))}
            </select>
          </label>
          {input.kind === 'pv' && (
            <>
              <label className="block text-xs text-gray-400">
                MPPT min V
                <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={input.mpptVoltageMin ?? ''} onChange={(e) => onPatch(input.id, { mpptVoltageMin: num(e.target.value, input.mpptVoltageMin) })} />
              </label>
              <label className="block text-xs text-gray-400">
                MPPT max V
                <input type="number" className="w-full mt-0.5 bg-black/20 rounded px-2 py-1.5 text-sm" value={input.mpptVoltageMax ?? ''} onChange={(e) => onPatch(input.id, { mpptVoltageMax: num(e.target.value, input.mpptVoltageMax) })} />
              </label>
            </>
          )}
        </div>
      )}
      <p className="text-xs text-gray-500 mt-2">
        Same min and max is a single rail (12V). Choose watts or amps — the solver uses only that rating.
      </p>
    </div>
  );
}

function standardsLabel(list: PoEStandard[]): string {
  return list.map((s) => POE_STANDARD_LABEL[s]).join(', ') || 'none';
}

function poeInSummary(spec: PoEInSpec): string {
  return [standardsLabel(spec.standards), formatVoltageRange(spec.voltageMin, spec.voltageMax), spec.maxWatts != null ? `${spec.maxWatts}W` : '']
    .filter(Boolean)
    .join(' · ');
}

function poeOutSummary(spec: PoEOutSpec): string {
  return [standardsLabel(spec.standards), `${spec.perPortMaxWatts}W`, spec.outputFollowsInputVoltage ? 'follows input' : '']
    .filter(Boolean)
    .join(' · ');
}

function toggleStd(list: PoEStandard[] | undefined, std: PoEStandard): PoEStandard[] {
  const cur = list || [];
  return cur.includes(std) ? cur.filter((s) => s !== std) : [...cur, std];
}

function RolePanel({
  role,
  ports,
  canPoeOut,
  produceable,
  hoverPortId,
  onHoverPort,
  onRename,
  onToggleEnabled,
  onChangeSpec,
  onTogglePort,
  onSelectAll,
  onClear,
  onTogglePassthrough,
}: {
  role: DevicePortRole;
  ports: FacedPort[];
  canPoeOut: boolean;
  produceable: PoEStandard[];
  hoverPortId: string | null;
  onHoverPort: (layoutKey: string | null, face?: EquipmentFace) => void;
  onRename: (name: string) => void;
  onToggleEnabled: (enabled: boolean) => void;
  onChangeSpec: (spec: PoEInSpec | PoEOutSpec) => void;
  onTogglePort: (key: string, on: boolean) => void;
  onSelectAll: () => void;
  onClear: () => void;
  onTogglePassthrough: (key: string, on: boolean) => void;
}) {
  const copper = ports.filter((p) => String(p.kind) === 'rj45');
  const assigned = copper.filter((p) => portHasRole(p, role)).length;
  const applyDisabled = role.kind === 'poe-out' && !canPoeOut;
  return (
    <div className="flex flex-col gap-2.5 text-sm border-t border-white/10 pt-2.5">
      <label className="block">
        Role Name
        <input
          className="w-full mt-1 bg-black/20 border border-white/10 rounded px-2.5 py-1.5"
          value={role.name}
          onChange={(e) => onRename(e.target.value)}
        />
      </label>
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={role.enabled !== false}
          onChange={(e) => onToggleEnabled(e.target.checked)}
        />
        Enabled
      </label>
      {applyDisabled && (
        <p className="text-amber-400 text-xs">PoE-out needs a power input before you can apply it to ports.</p>
      )}
      <div>
        <div className="text-xs font-medium text-gray-300 mb-1">Capabilities</div>
        <div className="space-y-1">
          {role.kind === 'poe-in' && role.poeIn && (
            <PoeInFields spec={role.poeIn} onChange={onChangeSpec} />
          )}
          {role.kind === 'poe-out' && role.poeOut && (
            <PoeOutFields spec={role.poeOut} produceable={produceable} onChange={onChangeSpec} />
          )}
        </div>
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <div className="text-xs font-medium text-gray-300 flex-1">Apply to ports</div>
          <button type="button" className="px-2 py-0.5 rounded bg-white/10 disabled:opacity-40 text-xs" disabled={applyDisabled} onClick={onSelectAll}>
            All RJ45
          </button>
          <button type="button" className="px-2 py-0.5 rounded bg-white/10 text-xs" onClick={onClear}>
            None
          </button>
        </div>
        <div className="text-xs text-gray-400 mb-0.5">{assigned} of {copper.length} copper ports</div>
        <div onMouseLeave={() => onHoverPort(null)}>
          {ports.map((p) => {
            const key = facedPortKey(p);
            const layoutKey = layoutPortKey(p);
            const copperPort = String(p.kind) === 'rj45';
            const checked = portHasRole(p, role);
            const other = role.kind === 'poe-in'
              ? (p.poeInRoleId && p.poeInRoleId !== role.id ? 'other PoE-in' : '')
              : (p.poeOutRoleId && p.poeOutRoleId !== role.id ? 'other PoE-out' : '');
            const disabled = !copperPort || applyDisabled;
            const hovered = hoverPortId === layoutKey;
            const look = poeRj45Look(p);
            return (
              <div
                key={key}
                className={`flex items-center gap-1 h-5 px-1 rounded text-xs leading-none ${disabled ? 'opacity-50' : hovered ? 'bg-white/10' : 'hover:bg-white/5'}`}
                onMouseEnter={() => onHoverPort(layoutKey, p.face)}
              >
                <label className="flex-1 min-w-0 flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-3.5 h-3.5 shrink-0"
                    disabled={disabled}
                    checked={checked}
                    onChange={(e) => onTogglePort(key, e.target.checked)}
                  />
                  <PoeRj45Marks look={look} variant="inline" />
                  <span className="truncate">
                    {p.face} {p.label || p.number} · {p.kind}
                    {look !== 'data' ? ` · ${poeRj45Caption(look)}` : ''}
                    {other ? ` · ${other}` : ''}
                  </span>
                </label>
                {copperPort && (
                  <label className="shrink-0 flex items-center gap-1 text-gray-400">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5"
                      checked={!!p.passthrough}
                      onChange={(e) => onTogglePassthrough(key, e.target.checked)}
                    />
                    Pass
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PoeInFields({ spec, onChange }: { spec: PoEInSpec; onChange: (s: PoEInSpec) => void }) {
  return (
    <>
      <div>Accepts</div>
      {POE_STANDARDS.map((s) => (
        <label key={s} className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={spec.standards.includes(s)}
            onChange={() => onChange({ ...spec, standards: toggleStd(spec.standards, s) })}
          />
          {POE_STANDARD_LABEL[s]}
        </label>
      ))}
      <div className="grid grid-cols-3 gap-1.5 pt-1">
        <label className="text-gray-400">
          Min V
          <input
            type="number"
            className="w-full bg-black/20 rounded px-2 py-1.5 text-gray-100"
            value={spec.voltageMin}
            onChange={(e) => onChange({ ...spec, voltageMin: Number(e.target.value) })}
          />
        </label>
        <label className="text-gray-400">
          Max V
          <input
            type="number"
            className="w-full bg-black/20 rounded px-2 py-1.5 text-gray-100"
            value={spec.voltageMax}
            onChange={(e) => onChange({ ...spec, voltageMax: Number(e.target.value) })}
          />
        </label>
        <label className="text-gray-400">
          Max W
          <input
            type="number"
            className="w-full bg-black/20 rounded px-2 py-1.5 text-gray-100"
            value={spec.maxWatts ?? ''}
            onChange={(e) => onChange({ ...spec, maxWatts: e.target.value === '' ? undefined : Number(e.target.value) })}
          />
        </label>
      </div>
    </>
  );
}

function PoeOutFields({
  spec,
  produceable,
  onChange,
}: {
  spec: PoEOutSpec;
  produceable: PoEStandard[];
  onChange: (s: PoEOutSpec) => void;
}) {
  return (
    <>
      {POE_STANDARDS.map((s) => {
        const allowed = spec.outputFollowsInputVoltage || produceable.includes(s);
        return (
          <label key={s} className={`flex items-center gap-1.5 ${allowed ? '' : 'opacity-40'}`}>
            <input
              type="checkbox"
              disabled={!allowed}
              checked={spec.standards.includes(s)}
              onChange={() => allowed && onChange({ ...spec, standards: toggleStd(spec.standards, s) })}
            />
            {POE_STANDARD_LABEL[s]}
          </label>
        );
      })}
      <label className="block mt-1 text-gray-400">
        Max Output per Port (Watts)
        <input
          type="number"
          className="w-full bg-black/20 rounded px-2 py-1.5 text-gray-100"
          value={spec.perPortMaxWatts}
          onChange={(e) => onChange({ ...spec, perPortMaxWatts: Number(e.target.value) })}
        />
      </label>
      <label className="flex items-center gap-1.5 mt-1">
        <input
          type="checkbox"
          checked={!!spec.outputFollowsInputVoltage}
          onChange={(e) => onChange({ ...spec, outputFollowsInputVoltage: e.target.checked })}
        />
        DC Output Voltage follows Input Voltage
      </label>
    </>
  );
}

export default ElectricalModelPanel;
